import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {forkJoin, Observable, Subscription} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {environment} from '../../../../environments/environment';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {ReferentialService} from '../../../shared/services/referential/referential.service';
import {TranslateService} from '@ngx-translate/core';
import {StatesService} from '../../../shared/services/state/states.service';
import {ProjectAlarmsCrudComponent} from '../project-alarms-crud/project-alarms-crud.component';
import {faEdit, faEye, faPlus, faTrashAlt} from '@fortawesome/free-solid-svg-icons';
import {AlarmService} from '../../../shared/services/alarm/alarm.service';
import {ProjectService} from '../../../shared/services/project/project.service';
import {AlarmType} from '../../../shared/classes/referential/alarm-type';
import {ModalService} from '../../../shared/services/modal/modal.service';
import {AuthenticationService} from '../../../shared/services/authentication/authentication.service';
import {LevelService} from '../../../shared/services/level/level.service';
import {Level} from '../../../model/level';

@Component({
  selector: 'app-project-alarms',
  templateUrl: './project-alarms.component.html',
  styleUrls: ['./project-alarms.component.css']
})
export class ProjectAlarmsComponent implements OnInit, OnDestroy {

  public faEye = faEye;
  public faEdit = faEdit;
  public faTrash = faTrashAlt;
  public alarmsList: MatTableDataSource<any>;
  public displayedColumns: string[] = ['id', 'project', 'type', 'name', 'actions'];
  public showSpinner: boolean;
  public filter = '';
  public pageSizeOptions = environment.pageSizeOptions;
  public pageSize = environment.pageSize;
  public idProject: number;
  public alarmTypes: AlarmType[];
  public selectedAlarmTypeName = 'liris-location-alarm';
  public activeAlarm = true;
  public faPlus = faPlus;
  public projectName = '';
  public alarmIsLevelValidable: boolean = false;


  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  private reloadProjectAlarms: Subscription;

  constructor(private alarmService: AlarmService,
              private route: ActivatedRoute,
              private router: Router,
              private toastr: ToastrService,
              private referentialService: ReferentialService,
              private translate: TranslateService,
              private modalService: ModalService,
              public statesService: StatesService,
              private projectService: ProjectService,
              private levelService: LevelService) {
  }

  ngOnInit() {
    this.idProject = parseInt(this.route.snapshot.paramMap.get('idProject'), 10);
    this.showSpinner = true;
    forkJoin(
      this.getProject(),
      this.getAlarmTypes(),
      this.setAlarmIsLevelValidable(),
      this.getProjectAlarmsList()
    ).subscribe(
      () => this.showSpinner = false,
      () => this.showSpinner = false,
    );
    this.reloadProjectAlarms = this.statesService.reloadProjectAlarms
      .pipe(
        switchMap(() => this.getProjectAlarmsList())
      )
      .subscribe(
        () => this.showSpinner = false,
        () => this.showSpinner = false,
      );
  }

  ngOnDestroy(): void {
    this.reloadProjectAlarms.unsubscribe();
  }

  openModal(mode: string, alarmId: number = null) {
    const modalRef = this.modalService.openNgbModal(ProjectAlarmsCrudComponent, {size: 'lg'});
    modalRef.componentInstance.projectId = parseInt(this.route.snapshot.paramMap.get('idProject'), 10);
    modalRef.componentInstance.readOnly = mode === 'read';
    modalRef.componentInstance.mode = mode;
    modalRef.componentInstance.alarmId = alarmId;
    modalRef.componentInstance.alarmIsLevelValidable = this.alarmIsLevelValidable;
  }

  deleteAlarm(alarmId: number) {
    this.showSpinner = true;
    this.alarmService.deleteAlarm(alarmId)
      .pipe(
        tap(() => {
          this.toastr.success(this.translate.instant('ALARM_DELETED'), this.translate.instant('SUCCESS'));
        }),
        switchMap(() => this.getProjectAlarmsList())
      )
      .subscribe(
        () => this.showSpinner = false,
        () => this.showSpinner = false
      );
  }

  getActiveTextForSlider() {
    let active = '';
    let inactive = '';
    this.translate.get('ACTIVE').subscribe((text: string) => {
      active = text;
    });
    this.translate.get('INACTIVE').subscribe((text: string) => {
      inactive = text;
    });
    return this.activeAlarm ? active : inactive;
  }

  reLoadList() {
    this.activeAlarm = !this.activeAlarm;
    this.getProjectAlarmsListAndSubscribe();

  }

  filterListByAlarmType(type: string) {
    if (this.selectedAlarmTypeName !== type) {
      this.selectedAlarmTypeName = type;
    } else {
      this.selectedAlarmTypeName = '';
    }
    this.getProjectAlarmsListAndSubscribe();

  }

  applyFilter(filterValue: string) {
    filterValue = filterValue.trim();
    filterValue = filterValue.toLowerCase();
    this.alarmsList.filter = filterValue;
  }

  private getProject() {
    return this.projectService.getProject(this.idProject).pipe(
      tap((project: any) => {
        this.projectName = project.name;
        this.showSpinner = false;
      })
    );
  }

  private setAlarmIsLevelValidable() {
    return this.levelService.getLevel(this.statesService.getSelectedLevelId()).pipe(
      tap((level: Level) => {
        this.alarmIsLevelValidable = level.alarm_validation;
      })
    );
  }

  private getAlarmTypes(): Observable<AlarmType[]> {
    return this.referentialService.getReferential('alarm-type').pipe(
      tap((alarmTypes: AlarmType[]) => this.alarmTypes = alarmTypes)
    );
  }

  private getProjectAlarmsList() {
    this.showSpinner = true;
    return this.alarmService.getProjectsAlarms(this.idProject, this.activeAlarm, this.selectedAlarmTypeName)
      .pipe(
        tap((alarms: any) => {
          this.alarmsList = new MatTableDataSource(alarms);

          this.alarmsList.sort = this.sort;
          this.alarmsList.paginator = this.paginator;
          this.alarmsList.paginator.firstPage();
          this.alarmsList.filter = '';
          this.filter = '';
        })
      );
  }

  private getProjectAlarmsListAndSubscribe() {
    this.getProjectAlarmsList().subscribe(
      () => this.showSpinner = false,
      () => this.showSpinner = false,
    );
  }

}
